import * as React from "react";
import "./CurrencyCtl.scss";

interface ICurrencyProps {
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    value?: number;
    onChange?: (value: number) => void;
    onBlur?: () => void;
    changeOverride?: () => void;
    readOnly?: boolean;
    showLock?: boolean;
    isLocked?: boolean;
}

interface ICurrencyState {
    caret: number;
    value: string;
}

export default class CurrencyCtl extends React.PureComponent<ICurrencyProps, ICurrencyState> {
    constructor(props: ICurrencyProps) {
        super(props);
        this.state = { caret: 0, value: this.formatCurrency(this.props.value ?? 0) };
    }

    _inputRef = React.createRef<HTMLInputElement>();

    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        // Get the changed value and insert a caret indicator
        let caret = e.currentTarget.selectionStart ?? 0;
        let value = e.currentTarget.value.substring(0, caret) + "^" + e.currentTarget.value.substring(caret);

        // Remove the placeholder leading zero if we added the first digit
        if (/^0.\d\d$/.test(this.state.value) && /^\d\^0.\d\d$/.test(value))
            value = value.substr(0, 2) + value.substr(3);

        let { int, dec, extraParts } = this.getCurrencyParts(value);
        if (int === "^") int = "^0";

        value = `${this.formatDigitGroups(int)}.${dec}`;

        this.props.onChange?.(parseFloat(`${int}.${dec}`.replace(/[,^]/, "")));

	    // Position the caret after the last decimal point if multiple decimal groups 
        caret = value.indexOf("^");
        value = value.replace("^", "");
	    if (extraParts) caret = value.indexOf(".") + 1;

        if (this.state.caret === caret && this.state.value === value)
            this.forceUpdate(); // We force the update so the caret position gets set
        else
            this.setState({ caret: caret, value: value });
    }

    getCurrencyParts(value: string): { int: string, dec: string, extraParts: boolean } {
        // Remove invalid characters, split into integer and decimal parts, remove leading zeros, and ensure decimal has two digits
        const parts: string[] = value.replace(/[^0-9.^]/g, "").split(".");
	    const int = (parts[0] || "0").substr((parts[0] || "0").split("").findIndex((d: string) => d !== "0"));
	    const dec = parts.length > 1 ? `${parts[parts.length - 1]}00`.substr(0, 2) : "00";
        return { int: int, dec: dec, extraParts: parts.length > 2 };
    }

    formatDigitGroups(value: string): string {
        let d = 0;
        for (var i = value.length - 1; i >= 0; i--) {
            if (/[0-9]/.test(value[i])) d++;
            if (d === 3 && i > 0) {
                value = value.substr(0, i) + "," + value.substr(i);
                d = 0;
            }
        }
        return value;
    }

    formatCurrency(value: number): string {
        const { int, dec } = this.getCurrencyParts(value.toFixed(2));
	    return `${this.formatDigitGroups(int)}.${dec}`;
    }

    componentDidUpdate() {
        const inp: HTMLInputElement | null = this._inputRef.current;
        if (inp)
            inp.setSelectionRange(this.state.caret, this.state.caret);
    }

    render() {
        return <div className={this.props.className ?? "currency"}><input ref={this._inputRef} onBlur={this.props.onBlur}
            type="text" className={"input" + (this.props.disabled ? " disabled" : "")}
            value={this.formatCurrency(this.props.value ?? 0)}
            placeholder={this.props.placeholder}
            onChange={this.onChange}
            disabled={this.props.disabled}
            readOnly={this.props.readOnly || this.props.isLocked} />
            {this.props.showLock && <button className="lock" onClick={() => this.props.changeOverride?.() } disabled={this.props.disabled}>
                <div style={{ width: '14px', margin: '20px 7px 0 5px' }}>
                    {this.props.isLocked &&
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path fill="#AA0000" d="M16,9V6c0-3.313-2.686-6-6-6C6.686,0,4,2.687,4,6v3H3v8.916C3,17.916,3,20,5.078,20c0.724,0,2.745,0,4.922,0 c2.178,0,4.198,0,4.922,0C17,20,17,17.916,17,17.916V9H16z M14,9h-4H6V6c0-2.211,1.791-4,4-4s4,1.789,4,4V9z" />
                        </svg>
                    }
                    {!this.props.isLocked &&
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path fill="#888888" d="M16,9.001v-3c0-3.313-2.687-6-6-6s-6,2.687-6,6h2c0-2.211,1.791-4,4-4c2.209,0,4,1.789,4,4v3h-4H6.604H6H4H3 v8.914c0,0,0,2.084,2.078,2.084c0.724,0,2.745,0,4.922,0c2.177,0,4.197,0,4.922,0C17,19.999,17,17.915,17,17.915V9.001H16z" />
                        </svg>
                    }
                    </div>
                </button>
            }
            </div>;
    }
}